"use client";

import { FC, useEffect, useState } from "react";
import Link from "next/link";
import Image from "next/image";

interface Reason {
  id: number;
  title: string;
  description: string;
  image: string;
}

const WhyRenvixxWorks: FC = () => {
  const [reasons, setReasons] = useState<Reason[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchReasons = async () => {
      try {
        const res = await fetch("https://portal.renvixx.com/api/why-choose-us");
        if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
        const contentType = res.headers.get("content-type");
        if (!contentType || !contentType.includes("application/json")) {
          throw new Error("Received non-JSON response from server");
        }
        const data = await res.json();
        if (data.success) {
          setReasons(data.data);
        }
      } catch (error) {
        console.error("Failed to fetch reasons:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchReasons();
  }, []);

  if (loading) {
    return (
      <section className="bg-[#2a6380] text-white py-20 font-outfit text-center">
        <p>Loading...</p>
      </section>
    );
  }

  return (
    <section className="bg-[#2a6380] text-white py-20 font-outfit">
      {/* Section Heading */}
      <div className="text-center mb-14 px-4">
        <p className="text-sm text-gray-300 mb-2">Why Choose Us</p>
        <h2 className="text-3xl sm:text-4xl font-bold">
          Why <span className="text-[#33c1c7]">Renvixx</span> Works?
        </h2>
      </div>

      {/* Cards */}
      <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 ">
        {reasons.map((item) => (
          <div
            key={item.id}
            className="bg-white text-[#2a6380] rounded-2xl border-2 border-[#33c1c7] shadow-sm p-8 flex flex-col items-center text-center transform transition-all duration-300 ease-in-out hover:scale-105 hover:shadow-lg"
          >
            <div className="flex items-center justify-center bg-[#2a6380] rounded-full w-16 h-16 mb-5 transition-all duration-300 ease-in-out">
              <Image
                src={item.image}
                alt={item.title}
                width={32}
                height={32}
              />
            </div>
            <h3 className="text-lg font-semibold mb-2 transition-colors text-[#000000] duration-300 ease-in-out">
              {item.title}
            </h3>
            <p
              className="text-sm text-gray-600 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: item.description }}
            ></p>
          </div>
        ))}
      </div>

      {/* Button */}
      <div className="text-center mt-12">
        <Link
          href="/team"
          className="relative overflow-hidden inline-block border-2 border-[#33c1c7] bg-[#33c1c7] text-[#2a6380] text-sm font-semibold px-8 py-3 rounded transition-all duration-500 ease-in-out group"
        >
          <span className="relative z-10 transition-colors duration-500 group-hover:text-[#33c1c7]">
            Meet Our Team
          </span>
          <span className="absolute inset-0 bg-[#2a6380] scale-[-1] -translate-x-full group-hover:translate-x-0 transition-transform duration-500 ease-in-out"></span>
        </Link>
      </div>
    </section>
  );
};

export default WhyRenvixxWorks;
